import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Divider,
    Alert
} from "@mui/material";
import {
    Schedule as ScheduleIcon,
    Save as SaveIcon
} from "@mui/icons-material";

const ScheduledScanDialog = ({ open, onClose, onSave, domain, loading }) => {
    const [targetUrl, setTargetUrl] = useState('');
    const [frequency, setFrequency] = useState('daily');
    const [timeOfDay, setTimeOfDay] = useState('00:00');
    const [maxPages, setMaxPages] = useState(100);
    const [maxDepth, setMaxDepth] = useState(3);
    const [delay, setDelay] = useState(2000);
    const [trackPriceChanges, setTrackPriceChanges] = useState(true);
    const [detectNewProducts, setDetectNewProducts] = useState(true);
    const [priceChangeThreshold, setPriceChangeThreshold] = useState(5);

    useEffect(() => {
        if (domain) {
            setTargetUrl(`https://${domain}`);
        }
    }, [domain]);

    const handleSave = () => {
        if (!domain || !targetUrl) {
            alert('Domain and target URL are required');
            return;
        }

        const config = {
            target_url: targetUrl,
            schedule: {
                frequency,
                time_of_day: frequency === 'daily' ? timeOfDay : undefined
            },
            scan_config: {
                max_pages: maxPages,
                max_depth: maxDepth,
                delay_between_requests: delay,
                respect_robots_txt: true,
                follow_external_links: false
            },
            monitoring: {
                track_price_changes: trackPriceChanges,
                detect_new_products: detectNewProducts,
                price_change_threshold_percent: priceChangeThreshold,
                notify_on_price_change: false,
                detect_out_of_stock: true
            }
        };

        onSave(domain, config);
    };

    const getFrequencyDescription = () => {
        switch (frequency) {
            case 'hourly':
                return 'Rescan every hour';
            case 'daily':
                return `Rescan daily at ${timeOfDay}`;
            case 'weekly':
                return 'Rescan once per week';
            case 'monthly':
                return 'Rescan once per month';
            default:
                return '';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    <Typography variant="h6">
                        Configure Scheduled Rescan
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Alert severity="info">
                        Enable periodic rescanning to track price changes and detect new products automatically
                    </Alert>

                    <TextField
                        label="Domain"
                        value={domain}
                        disabled
                        fullWidth
                    />

                    <TextField
                        label="Target URL"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        fullWidth
                        required
                        helperText="Starting URL for the scan"
                    />

                    <Divider>Schedule</Divider>

                    <FormControl fullWidth>
                        <InputLabel>Frequency</InputLabel>
                        <Select
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value)}
                            label="Frequency"
                        >
                            <MenuItem value="hourly">Hourly</MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>

                    {frequency === 'daily' && (
                        <TextField
                            label="Time of Day"
                            type="time"
                            value={timeOfDay}
                            onChange={(e) => setTimeOfDay(e.target.value)}
                            fullWidth
                            helperText="Time when daily scan should run"
                        />
                    )}

                    <Alert severity="success" icon={<ScheduleIcon />}>
                        {getFrequencyDescription()}
                    </Alert>

                    <Divider>Scan Configuration</Divider>

                    <TextField
                        label="Max Pages"
                        type="number"
                        value={maxPages}
                        onChange={(e) => setMaxPages(parseInt(e.target.value) || 100)}
                        fullWidth
                    />

                    <TextField
                        label="Max Depth"
                        type="number"
                        value={maxDepth}
                        onChange={(e) => setMaxDepth(parseInt(e.target.value) || 3)}
                        fullWidth
                    />

                    <TextField
                        label="Delay (ms)"
                        type="number"
                        value={delay}
                        onChange={(e) => setDelay(parseInt(e.target.value) || 2000)}
                        fullWidth
                        helperText="Delay between requests in milliseconds"
                    />

                    <Divider>Monitoring</Divider>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={trackPriceChanges}
                                onChange={(e) => setTrackPriceChanges(e.target.checked)}
                            />
                        }
                        label="Track Price Changes"
                    />

                    {trackPriceChanges && (
                        <TextField
                            label="Price Change Threshold (%)"
                            type="number"
                            value={priceChangeThreshold}
                            onChange={(e) => setPriceChangeThreshold(parseInt(e.target.value) || 5)}
                            fullWidth
                            helperText="Alert for price changes above this percentage"
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                checked={detectNewProducts}
                                onChange={(e) => setDetectNewProducts(e.target.checked)}
                            />
                        }
                        label="Detect New Products"
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading || !domain || !targetUrl}
                    startIcon={<SaveIcon />}
                >
                    Save Configuration
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduledScanDialog;
